import {
    AlertTriangle,
    ArrowRight,
    CheckCircle,
    Clock,
    CreditCard02,
    FilePlus02,
    Play,
    RefreshCw02,
    Send01,
    XCircle,
} from '@untitledui/icons'
import clsx from 'clsx'
import RunHistory from 'components/billing/billingRun/RunHistory'
import { formatDuration, runStatusMeta } from 'components/billing/billingRun/runUi'
import { Card, HelpTip } from 'components/billing/debtorNew/ui'
import { format } from 'date-fns'
import {
    BILLING_RUN_STEPS,
    BillingRunStep,
    BillingRunStepName,
    useBillingRuns,
} from 'hooks/billingRunHooks'
import { ComponentType, Fragment, useState } from 'react'

interface StepMeta {
    step: BillingRunStepName
    nr: number
    title: string
    tagline: string
    help: string
    icon: ComponentType<{ className?: string }>
}

const STEPS: StepMeta[] = [
    {
        step: BILLING_RUN_STEPS.GENERATE_INVOICES,
        nr: 1,
        title: 'Generate invoices',
        tagline: 'Invoice all open charges of the month',
        help: 'Creates invoices for all open charges of the month (direct debit + manual transfer). Charges without payment info are skipped — see the audit tab.',
        icon: FilePlus02,
    },
    {
        step: BILLING_RUN_STEPS.SEND_MAILS,
        nr: 2,
        title: 'Send invoice mails',
        tagline: 'Mail every invoice that was not mailed yet',
        help: 'Mails every regular monthly invoice (not interim, not credit, not lost) that has no invoice mail yet. Double-send protection stays active per mail.',
        icon: Send01,
    },
    {
        step: BILLING_RUN_STEPS.CREATE_TRANSACTIONS,
        nr: 3,
        title: 'Create transactions',
        tagline: 'Direct-debit every eligible invoice via Mollie',
        help: 'Creates Mollie direct-debit payments for every eligible invoice of the month that has no transaction yet. Usually done a few days after the mails, so corrected invoices are picked up automatically.',
        icon: CreditCard02,
    },
]

interface CountChipProps {
    value: number
    label: string
    tone: string
}

const CountChip = ({ value, label, tone }: CountChipProps) => (
    <span className={clsx('rounded-md px-1.5 py-0.5 text-sm font-medium tabular-nums', tone)}>
        {value} {label}
    </span>
)

interface ProgressBarProps {
    run: BillingRunStep
}

const ProgressBar = ({ run }: ProgressBarProps) => {
    const isRunning = run.status === 'running'
    const percentage = (() => {
        if (run.total > 0) {
            return Math.min((run.processed / run.total) * 100, 100)
        }
        return run.status === 'finished' ? 100 : 0
    })()

    return (
        <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-neutral-100">
            <div
                className={clsx(
                    'h-full rounded-full transition-all duration-700',
                    run.status === 'failed' || run.is_stale ? 'bg-red-400' : 'bg-primary-500',
                    isRunning && 'animate-pulse',
                )}
                style={{ width: `${percentage}%` }}
            />
        </div>
    )
}

interface RunSummaryProps {
    run: BillingRunStep
    onShowErrors: () => void
}

/** Progress + counts + meta line for the step's most recent run. */
const RunSummary = ({ run, onShowErrors }: RunSummaryProps) => {
    const isRunning = run.status === 'running' && !run.is_stale
    const duration = formatDuration(run.started, run.finished)

    return (
        <div className="flex flex-col gap-2">
            <ProgressBar run={run} />
            <div className="flex items-center justify-between text-sm tabular-nums text-neutral-500">
                <span>
                    {run.processed} / {run.total} processed
                </span>
                {isRunning && (
                    <span className="flex items-center gap-1 font-medium text-primary-600">
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary-500" />
                        live
                    </span>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
                <CountChip value={run.succeeded} label="ok" tone="bg-green-50 text-green-700" />
                {run.failed > 0 && (
                    <button
                        type="button"
                        onClick={onShowErrors}
                        title="Open the error report of this run"
                        className="flex items-center gap-1 rounded-md bg-red-50 px-1.5 py-0.5 text-sm font-medium tabular-nums text-red-700 transition hover:bg-red-100">
                        {run.failed} failed
                        <ArrowRight className="h-3 w-3" />
                    </button>
                )}
                {run.skipped > 0 && (
                    <CountChip
                        value={run.skipped}
                        label="skipped"
                        tone="bg-neutral-100 text-neutral-600"
                    />
                )}
            </div>

            <p className="flex items-center gap-1 text-sm text-neutral-400">
                <Clock className="h-3.5 w-3.5" />
                {format(new Date(run.started), 'd MMM HH:mm')}
                {duration && ` · ${duration}`}
                {run.triggered_by_user && ` · ${run.triggered_by_user.username}`}
            </p>
        </div>
    )
}

interface StepCardProps {
    meta: StepMeta
    month: string
    countries: string[]
    lastRun?: BillingRunStep
    isBlocked: boolean
    onStart: () => void
    onShowErrors: (runId: number) => void
}

const StepCard = ({
    meta,
    month,
    countries,
    lastRun,
    isBlocked,
    onStart,
    onShowErrors,
}: StepCardProps) => {
    const [isConfirming, setIsConfirming] = useState(false)
    const isRunning = lastRun?.status === 'running' && !lastRun.is_stale
    const isStale = lastRun?.is_stale === true
    const status = lastRun ? runStatusMeta(lastRun) : null
    const Icon = meta.icon

    return (
        <Card
            className={clsx(
                'flex min-w-0 flex-1 flex-col gap-3 p-4 transition',
                isRunning && 'border-primary-200 shadow-xs',
            )}>
            <div className="flex items-start gap-3">
                <div
                    className={clsx(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                        isRunning
                            ? 'bg-primary-50 text-primary-600'
                            : 'bg-neutral-50 text-neutral-500',
                    )}>
                    <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                        <HelpTip help={meta.help}>
                            <span className="text-md font-semibold text-dark">
                                {meta.nr} · {meta.title}
                            </span>
                        </HelpTip>
                        {status && (
                            <span
                                className={clsx(
                                    'flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-sm font-medium',
                                    status.pill,
                                )}>
                                <span
                                    className={clsx(
                                        'h-1.5 w-1.5 rounded-full',
                                        status.dot,
                                        isRunning && 'animate-pulse',
                                    )}
                                />
                                {status.label}
                            </span>
                        )}
                    </div>
                    <p className="mt-0.5 truncate text-sm text-neutral-400">{meta.tagline}</p>
                </div>
            </div>

            {lastRun ? (
                <RunSummary
                    run={lastRun}
                    onShowErrors={() => onShowErrors(lastRun.billing_run_step_id)}
                />
            ) : (
                <p className="rounded-lg bg-neutral-50 px-3 py-2.5 text-sm text-neutral-400">
                    Not run yet for {month}.
                </p>
            )}

            <div className="mt-auto pt-1">
                {isConfirming ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                        <p className="text-md font-semibold text-amber-800">
                            {isStale ? 'Resume' : 'Start'} &ldquo;{meta.title}&rdquo; for {month} (
                            {countries.join(', ')})?
                        </p>
                        <p className="mt-0.5 text-sm text-amber-700">
                            {isStale
                                ? 'Closes the stalled run and starts a new one that continues where it stopped — work already done is skipped.'
                                : 'Runs on the server — progress shows up on this card.'}
                        </p>
                        <div className="mt-2.5 flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    onStart()
                                    setIsConfirming(false)
                                }}
                                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-md font-semibold text-white transition hover:opacity-90">
                                <Play className="h-4 w-4" />
                                {isStale ? 'Yes, resume now' : 'Yes, start now'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsConfirming(false)}
                                className="rounded-lg bg-white px-3 py-2 text-md font-semibold text-neutral-600 shadow-xs ring-1 ring-neutral-200 transition hover:bg-neutral-50">
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <button
                            type="button"
                            onClick={() => setIsConfirming(true)}
                            disabled={isBlocked}
                            className={clsx(
                                'flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-md font-semibold transition',
                                isBlocked
                                    ? 'cursor-not-allowed bg-neutral-100 text-neutral-400'
                                    : 'bg-primary text-white hover:opacity-90',
                            )}>
                            {(() => {
                                // A run for other countries can be live while
                                // this button stays enabled for the selection.
                                if (isRunning && isBlocked) {
                                    return (
                                        <>
                                            <RefreshCw02 className="h-4 w-4 animate-spin" />
                                            Running…
                                        </>
                                    )
                                }
                                if (isStale) {
                                    return (
                                        <>
                                            <Play className="h-4 w-4" />
                                            Resume
                                        </>
                                    )
                                }
                                return (
                                    <>
                                        <Play className="h-4 w-4" />
                                        {lastRun ? 'Run again' : 'Start'}
                                    </>
                                )
                            })()}
                        </button>
                        {isBlocked && !isRunning && (
                            <p className="mt-1.5 text-center text-sm text-neutral-400">
                                A running step overlaps the selected countries — wait for it to
                                finish.
                            </p>
                        )}
                    </>
                )}
            </div>
        </Card>
    )
}

interface RunTabProps {
    month: string
}

const RunTab = ({ month }: RunTabProps) => {
    const { runs, startStep, abortRun, startError, countries } = useBillingRuns(month)
    const [expandedRunId, setExpandedRunId] = useState<number | null>(null)
    // Stale runs don't block: starting a step auto-closes its stale row
    // server-side, so the card can offer a one-click Resume instead.
    const runningRuns = runs.filter((run) => run.status === 'running' && !run.is_stale)

    const overlapsSelection = (run: BillingRunStep) =>
        countries.some((selected) =>
            run.countries
                .split(',')
                .map((code) => code.trim())
                .includes(selected),
        )

    // Cards and history only show runs of the selected countries — but the
    // blocking logic below deliberately looks at ALL runs: a generation run
    // for another country must still block generation here.
    const visibleRuns = runs.filter(overlapsSelection)
    const monthRuns = visibleRuns.filter((run) => run.about_month.startsWith(month))

    /** Most recent run of a step for the month within the selected countries. */
    const getLastVisibleRun = (step: BillingRunStepName) =>
        visibleRuns.find((run) => run.step === step && run.about_month.startsWith(month))

    /**
     * Mirrors the server-side guard (BillingRunStepsTable::start()):
     * generation is globally serialized, mails/transactions only conflict on
     * overlapping countries. On top, a run overlapping the selection blocks
     * the other steps too — the billing flow within a country is sequential.
     */
    const isStepBlocked = (step: BillingRunStepName) =>
        runningRuns.some((run) => {
            if (run.step === step && step === BILLING_RUN_STEPS.GENERATE_INVOICES) {
                return true
            }
            return overlapsSelection(run)
        })

    return (
        <div>
            {startError && (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-md text-red-700">
                    <XCircle className="h-4 w-4 shrink-0" />
                    {startError}
                </div>
            )}

            <div className="flex flex-col items-stretch gap-2 lg:flex-row lg:items-center">
                {STEPS.map((meta, index) => (
                    <Fragment key={meta.step}>
                        {index > 0 && (
                            <ArrowRight className="hidden h-4 w-4 shrink-0 text-neutral-300 lg:block" />
                        )}
                        <StepCard
                            meta={meta}
                            month={month}
                            countries={countries}
                            lastRun={getLastVisibleRun(meta.step)}
                            isBlocked={isStepBlocked(meta.step)}
                            onStart={() => startStep(meta.step)}
                            onShowErrors={setExpandedRunId}
                        />
                    </Fragment>
                ))}
            </div>

            <p className="mt-3 flex items-center gap-1.5 text-sm text-neutral-400">
                <CheckCircle className="h-3.5 w-3.5" />
                Every step runs server-side and is safe to re-run — it only picks up what is still
                missing. Finance usually waits a few days between mails and transactions.
            </p>

            {monthRuns.some((run) => run.is_stale) && (
                <div className="mt-3 flex items-center gap-2 rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                    <AlertTriangle className="h-4 w-4 shrink-0" />A run without recent progress is
                    marked as stalled — its worker probably died. Press Resume on the step card; it
                    continues where it stopped.
                </div>
            )}

            <h2 className="mb-3 mt-8 text-xl font-semibold text-dark">Run history</h2>
            <RunHistory
                runs={visibleRuns}
                onAbort={abortRun}
                expandedRunId={expandedRunId}
                onToggleExpand={setExpandedRunId}
            />
        </div>
    )
}

export default RunTab
